import { useCallback, useMemo } from "react";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { tools } from "@/navigation/tools";

type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    server: Promise<{ type: string; error?: { message: string } }>;
  };
};

/** The signed-in user's favorite tools, in the order of the tool catalog. */
export function useFavoriteTools() {
  const { user } = useAuth();
  const zero = useZero() as ZeroMutateClient;
  const query = user?.id ? appQueries.toolFavorites.listMine() : undefined;
  const [rows] = useZeroQuery(query, { enabled: !!user?.id });

  const favoriteIds = useMemo(
    () => new Set(((rows ?? []) as { toolID: string }[]).map((r) => r.toolID)),
    [rows],
  );
  const favoriteTools = useMemo(
    () => tools.filter((tool) => favoriteIds.has(tool.id)),
    [favoriteIds],
  );

  const setFavorite = useCallback(
    (toolID: string, favorite: boolean) => {
      const mutator = favorite
        ? appMutators.toolFavorites.add
        : appMutators.toolFavorites.remove;
      void zero.mutate(mutator({ toolID })).server.then((result) => {
        if (result.type === "error") {
          toast.error(result.error?.message ?? "Could not update favorites");
        }
      });
    },
    [zero],
  );

  return { favoriteIds, favoriteTools, setFavorite };
}
